import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiClock, FiEye, FiCalendar, FiArrowLeft, FiShare2, FiBookmark, FiEdit, FiTrash2 } from 'react-icons/fi';
import { FaFacebook, FaTwitter, FaLinkedin, FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { marked } from 'marked';
import { blogAPI } from '../services/blogAPI';
import { useAuth } from '../context/AuthContext';

// Configure marked for safe rendering
marked.setOptions({ breaks: true, gfm: true });

// Styles for rendered markdown content
const blogContentStyles = `
  .blog-content {
    font-size: 1.1rem;
    line-height: 1.85;
    color: #374151;
  }

  .blog-content > *:first-child { margin-top: 0; }
  .blog-content > *:last-child  { margin-bottom: 0; }

  .blog-content h1,
  .blog-content h2,
  .blog-content h3,
  .blog-content h4 {
    font-weight: 700;
    color: #111827;
    line-height: 1.3;
  }

  .blog-content h1 {
    font-size: 2rem;
    margin-top: 2.5rem;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #e5e7eb;
  }

  .blog-content h2 {
    font-size: 1.6rem;
    margin-top: 2.5rem;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #e5e7eb;
  }

  .blog-content h3 {
    font-size: 1.3rem;
    margin-top: 2rem;
    margin-bottom: 0.75rem;
    color: #1f2937;
  }

  .blog-content h4 {
    font-size: 1.1rem;
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
    color: #374151;
  }

  .blog-content p {
    margin-bottom: 1.25rem;
  }

  .blog-content strong {
    font-weight: 700;
    color: #111827;
  }

  .blog-content em {
    font-style: italic;
  }

  .blog-content ul,
  .blog-content ol {
    margin: 0.75rem 0 1.25rem 1.5rem;
    padding-left: 0.5rem;
  }

  .blog-content ul { list-style-type: disc; }
  .blog-content ol { list-style-type: decimal; }

  .blog-content ul ul { list-style-type: circle; }
  .blog-content ol ol { list-style-type: lower-alpha; }

  .blog-content li {
    margin-bottom: 0.4rem;
    padding-left: 0.25rem;
  }

  .blog-content li::marker { color: #0ea5e9; }

  .blog-content blockquote {
    border-left: 4px solid #0ea5e9;
    background: linear-gradient(to right, #f0f9ff, #ffffff);
    padding: 1.25rem 1.75rem;
    margin: 2rem 0;
    border-radius: 0 0.5rem 0.5rem 0;
    font-style: italic;
    color: #1e40af;
  }

  .blog-content blockquote p { margin-bottom: 0; }

  .blog-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 1.5rem 0;
    font-size: 0.95rem;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    border-radius: 0.5rem;
    overflow: hidden;
  }

  .blog-content th {
    background: linear-gradient(to right, #0ea5e9, #0284c7);
    color: white;
    font-weight: 600;
    padding: 0.875rem 1rem;
    text-align: left;
  }

  .blog-content td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .blog-content tr:nth-child(even) td { background: #f9fafb; }
  .blog-content tr:hover td { background: #f0f9ff; }

  .blog-content a {
    color: #0ea5e9;
    text-decoration: underline;
    font-weight: 500;
  }

  .blog-content a:hover { color: #0284c7; }

  .blog-content img {
    max-width: 100%;
    height: auto;
    border-radius: 0.75rem;
    margin: 1.5rem 0;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  }

  .blog-content hr {
    border: none;
    height: 2px;
    background: linear-gradient(to right, transparent, #e5e7eb, transparent);
    margin: 2.5rem 0;
  }

  .blog-content code {
    background: #f3f4f6;
    padding: 0.2rem 0.45rem;
    border-radius: 0.25rem;
    font-size: 0.875em;
    color: #dc2626;
    font-family: ui-monospace, monospace;
  }

  .blog-content pre {
    background: #1f2937;
    color: #f9fafb;
    padding: 1.5rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 1.5rem 0;
    font-family: ui-monospace, monospace;
  }

  .blog-content pre code {
    background: none;
    color: inherit;
    padding: 0;
    font-size: 0.9rem;
  }
`;

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    setLoading(true);
    try {
      const response = await blogAPI.getBlogBySlug(slug);
      setBlog(response.data);

      // Fetch related blogs
      if (response.data.category) {
        const relatedResponse = await blogAPI.getRelatedBlogs(response.data.id, response.data.category);
        setRelatedBlogs(relatedResponse.data);
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      toast.error('Blog not found');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await blogAPI.deleteBlog(blog.id);
        toast.success('Blog deleted successfully');
        navigate('/blog');
      } catch (error) {
        toast.error('Failed to delete blog');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      WEIGHT_LOSS_TIPS: 'bg-orange-100 text-orange-700',
      HEALTHY_RECIPES: 'bg-green-100 text-green-700',
      NUTRITION: 'bg-blue-100 text-blue-700',
      EXERCISE: 'bg-red-100 text-red-700',
      GLP1_MEDICATION: 'bg-purple-100 text-purple-700',
      MENTAL_HEALTH: 'bg-pink-100 text-pink-700',
      SUCCESS_STORIES: 'bg-yellow-100 text-yellow-700',
      LIFESTYLE: 'bg-teal-100 text-teal-700',
      SLEEP_WELLNESS: 'bg-indigo-100 text-indigo-700',
      METABOLISM: 'bg-cyan-100 text-cyan-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  // Parse markdown → HTML once when blog loads
  const contentHtml = useMemo(() => {
    if (!blog?.content) return '';
    // If content looks like HTML already, use as-is; otherwise parse markdown
    const isHtml = /<[a-z][\s\S]*>/i.test(blog.content);
    return isHtml ? blog.content : marked(blog.content);
  }, [blog?.content]);

  const shareUrl = window.location.href;
  const shareTitle = blog?.title || '';

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-sky-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-700 mb-4">Blog not found</h2>
          <Link to="/blog" className="text-sky-600 hover:text-sky-700">
            Go back to blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Inject custom styles */}
      <style>{blogContentStyles}</style>

      {/* Hero Image */}
      <div className="relative h-[50vh] md:h-[60vh]">
        <img
          src={blog.featuredImage || 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=1200'}
          alt={blog.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

        {/* Back Button */}
        <Link
          to="/blog"
          className="absolute top-6 left-6 flex items-center gap-2 text-white bg-black/30 hover:bg-black/50 px-4 py-2 rounded-full transition-colors backdrop-blur-sm"
        >
          <FiArrowLeft /> Back to Blog
        </Link>

        {/* Admin Actions */}
        {isAdmin && (
          <div className="absolute top-6 right-6 flex gap-2">
            <Link
              to={`/blog/edit/${blog.id}`}
              className="flex items-center gap-2 text-white bg-sky-600 hover:bg-sky-700 px-4 py-2 rounded-full transition-colors"
            >
              <FiEdit /> Edit
            </Link>
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded-full transition-colors"
            >
              <FiTrash2 /> Delete
            </button>
          </div>
        )}

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="max-w-4xl mx-auto">
            <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-semibold mb-4 ${getCategoryColor(blog.category)}`}>
              {blog.categoryDisplayName}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              {blog.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-gray-300">
              <div className="flex items-center">
                {blog.authorImage ? (
                  <img src={blog.authorImage} alt={blog.authorName} className="w-12 h-12 rounded-full mr-3 border-2 border-white/30" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center mr-3 border-2 border-white/30">
                    <span className="text-white font-bold text-lg">
                      {blog.authorName?.charAt(0) || 'F'}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-white font-semibold">{blog.authorName || 'Fitnession Team'}</p>
                  {blog.authorTitle && <p className="text-gray-400 text-sm">{blog.authorTitle}</p>}
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5">
                  <FiCalendar className="text-sky-400" /> {formatDate(blog.publishedAt || blog.createdAt)}
                </span>
                <span className="flex items-center gap-1.5">
                  <FiClock className="text-sky-400" /> {blog.readTime} min read
                </span>
                <span className="flex items-center gap-1.5">
                  <FiEye className="text-sky-400" /> {blog.viewCount} views
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Action Bar */}
        <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200">
          <div className="relative">
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="flex items-center gap-2 text-gray-600 hover:text-sky-600 transition-colors font-medium"
            >
              <FiShare2 className="text-lg" /> Share this article
            </button>
            {showShareMenu && (
              <div className="absolute top-full left-0 mt-3 bg-white shadow-xl rounded-xl p-4 z-10 border border-gray-100">
                <p className="text-xs text-gray-500 mb-3 font-medium">Share on</p>
                <div className="flex gap-3">
                  <a
                    href={shareLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors hover:scale-110"
                  >
                    <FaFacebook />
                  </a>
                  <a
                    href={shareLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors hover:scale-110"
                  >
                    <FaTwitter />
                  </a>
                  <a
                    href={shareLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-blue-700 text-white rounded-full flex items-center justify-center hover:bg-blue-800 transition-colors hover:scale-110"
                  >
                    <FaLinkedin />
                  </a>
                  <a
                    href={shareLinks.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600 transition-colors hover:scale-110"
                  >
                    <FaWhatsapp />
                  </a>
                </div>
              </div>
            )}
          </div>
          <button className="flex items-center gap-2 text-gray-600 hover:text-sky-600 transition-colors font-medium">
            <FiBookmark className="text-lg" /> Save for later
          </button>
        </div>

        {/* Summary */}
        {blog.summary && (
          <div className="bg-gradient-to-r from-sky-50 to-blue-50 border-l-4 border-sky-500 p-6 rounded-r-xl mb-10 shadow-sm">
            <p className="text-gray-700 text-lg italic leading-relaxed">{blog.summary}</p>
          </div>
        )}

        {/* Main Content */}
        <article className="bg-white rounded-2xl shadow-sm p-8 md:p-12 mb-10">
          <div
            className="blog-content"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />
        </article>

        {/* Tags / Category Link */}
        <div className="flex items-center gap-4 mb-10">
          <span className="text-gray-500 font-medium">Category:</span>
          <Link
            to={`/blog?category=${blog.category}`}
            className={`inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold ${getCategoryColor(blog.category)} hover:opacity-80 transition-all hover:scale-105`}
          >
            {blog.categoryDisplayName}
          </Link>
        </div>

        {/* Author Box */}
        <div className="bg-white rounded-2xl shadow-md p-8 mb-12 border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-6">About the Author</h3>
          <div className="flex items-start gap-6">
            {blog.authorImage ? (
              <img src={blog.authorImage} alt={blog.authorName} className="w-24 h-24 rounded-full object-cover border-4 border-sky-100" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center flex-shrink-0 border-4 border-sky-100">
                <span className="text-white text-3xl font-bold">
                  {blog.authorName?.charAt(0) || 'F'}
                </span>
              </div>
            )}
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-1">{blog.authorName || 'Fitnession Team'}</h4>
              {blog.authorTitle && (
                <p className="text-sky-600 font-medium mb-3">{blog.authorTitle}</p>
              )}
              <p className="text-gray-600 leading-relaxed">
                Our team of health experts, nutritionists, and fitness coaches are dedicated to helping you achieve your wellness goals through evidence-based guidance and personalized support.
              </p>
            </div>
          </div>
        </div>

        {/* Related Blogs */}
        {relatedBlogs.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center">
              <span className="w-1 h-8 bg-sky-500 rounded-full mr-3"></span>
              Related Articles
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedBlogs.map((relatedBlog) => (
                <Link
                  key={relatedBlog.id}
                  to={`/blog/${relatedBlog.slug}`}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 group border border-gray-100 hover:border-sky-200"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={relatedBlog.featuredImage || 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=400'}
                      alt={relatedBlog.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-gray-900 group-hover:text-sky-600 transition-colors line-clamp-2 mb-3">
                      {relatedBlog.title}
                    </h3>
                    <div className="flex items-center text-gray-400 text-sm">
                      <FiClock className="mr-1.5" /> {relatedBlog.readTime} min read
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-sky-600 via-sky-700 to-sky-800 rounded-2xl p-10 text-center text-white shadow-xl">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Transformation?</h2>
          <p className="text-sky-100 mb-8 max-w-xl mx-auto text-lg">
            Join thousands who have achieved their health goals with Fitnession's comprehensive GLP-1 program.
          </p>
          <Link
            to="/register"
            className="inline-block bg-white text-sky-600 font-bold px-10 py-4 rounded-full hover:bg-sky-50 transition-all hover:scale-105 shadow-lg"
          >
            Get Started Today
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogDetail;
